import FontAwesome from '@expo/vector-icons/FontAwesome'
import { type ComponentProps } from 'react'
import { Pressable, View } from 'react-native'
import { Text } from '@/components/ui/Text'
import { useThemeColors } from '@/contexts/ThemeContext'

export type ChoiceCardProps = {
  variant: 'icon' | 'tile'
  iconName: ComponentProps<typeof FontAwesome>['name']
  label: string
  subtitle?: string
  active?: boolean
  testID?: string
  onPress: () => void
}

export function ChoiceCard({
  variant,
  iconName,
  label,
  subtitle,
  active = false,
  testID,
  onPress,
}: ChoiceCardProps) {
  const colors = useThemeColors()

  const activeBg = colors.primary + '18'
  const borderColor = active ? colors.primary : colors.border
  const borderLeftWidth = active ? 3 : 1

  if (variant === 'tile') {
    return (
      <Pressable
        testID={testID}
        onPress={onPress}
        style={({ pressed }) => ({
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: 22,
          paddingHorizontal: 12,
          borderRadius: 16,
          borderWidth: 1,
          borderLeftWidth,
          borderColor,
          backgroundColor: pressed ? colors.primary + '28' : active ? activeBg : colors.surface,
          gap: 10,
          minHeight: 100,
        })}
      >
        <FontAwesome name={iconName} size={24} color={colors.primary} />
        <Text
          style={{
            fontWeight: '700',
            fontSize: 15,
            color: colors.dark,
            textAlign: 'center',
          }}
        >
          {label}
        </Text>
        {subtitle ? (
          <Text
            style={{
              fontSize: 12,
              color: colors.muted,
              textAlign: 'center',
              lineHeight: 16,
            }}
          >
            {subtitle}
          </Text>
        ) : null}
      </Pressable>
    )
  }

  // icon variant — full-width horizontal card
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderLeftWidth,
        borderColor,
        backgroundColor: pressed ? colors.primary + '28' : active ? activeBg : colors.surface,
        gap: 16,
      })}
    >
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          backgroundColor: colors.primary + '18',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FontAwesome name={iconName} size={18} color={colors.primary} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={{ fontWeight: '700', fontSize: 16, color: colors.dark }}>{label}</Text>
        {subtitle ? (
          <Text style={{ fontSize: 13, color: colors.muted, marginTop: 3, lineHeight: 18 }}>
            {subtitle}
          </Text>
        ) : null}
      </View>
    </Pressable>
  )
}
